import { useMemo, useState, Fragment } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api";
import {
  Inbox, Linkedin, ExternalLink, ShieldAlert, Database,
  Filter, MessageSquare, FileText, Hash, Lock, AlertCircle,
  ShieldQuestion, PaperclipIcon, XCircle,
  ChevronRight, ChevronDown,
} from "lucide-react";

// Bucket → header meta. Buckets are UI groupings; underlying LI status +
// Approval Status remain authoritative.
const BUCKET_META = {
  needs_attention:       { label: "Needs Attention",      color: "text-amber-300",  bg: "border-amber-800/60",   pill: "bg-amber-950/60 text-amber-300 border-amber-800/60",   icon: AlertCircle,
                            desc: "Verified/Open requests awaiting your review — act on these first." },
  verification_required: { label: "Identity Verification", color: "text-sky-300",   bg: "border-sky-800/60",     pill: "bg-sky-950/60 text-sky-300 border-sky-800/60",         icon: ShieldQuestion,
                            desc: "Upstream identity check pending. Drafting blocked until requester identity is captured." },
  submitted:             { label: "Proposal Submitted",   color: "text-emerald-300",bg: "border-emerald-800/60", pill: "bg-emerald-950/60 text-emerald-300 border-emerald-800/60", icon: PaperclipIcon,
                            desc: "Already submitted in LinkedIn. Monitor for response." },
  closed_declined:       { label: "Closed · Declined",    color: "text-slate-500",  bg: "border-slate-800",      pill: "bg-slate-800/60 text-slate-400 border-slate-700",      icon: XCircle,
                            desc: "Declined or closed. No further action." },
  other:                 { label: "Other",                color: "text-slate-500",  bg: "border-slate-800",      pill: "bg-slate-800/60 text-slate-500 border-slate-700",      icon: Inbox,
                            desc: "Uncategorized live state — review manually." },
};

const FIT_STYLE = {
  "Recommended":   "bg-emerald-950/60 text-emerald-300 border-emerald-800/60",
  "Investigate":   "bg-amber-950/60 text-amber-300 border-amber-800/60",
  "Do Not Pursue": "bg-rose-950/60 text-rose-300 border-rose-800/60",
};

const STATUS_STYLE = {
  "Open":                              "bg-emerald-950/60 text-emerald-300 border-emerald-800/60",
  "Open — Verified":                   "bg-emerald-950/60 text-emerald-300 border-emerald-800/60",
  "Identity Verification Required":    "bg-sky-950/60 text-sky-300 border-sky-800/60",
  "Proposal Submitted":                "bg-indigo-950/60 text-indigo-300 border-indigo-800/60",
  "Declined":                          "bg-slate-800/60 text-slate-400 border-slate-700",
  "No Longer Accepting Proposals":     "bg-slate-800/60 text-slate-400 border-slate-700",
};

function Badge({ children, className = "", testid, title }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono border ${className}`}
      data-testid={testid}
      title={title}
    >
      {children}
    </span>
  );
}

function fmtAge(days) {
  if (days == null) return "—";
  if (days === 0) return "today";
  if (days === 1) return "1d";
  if (days < 30) return `${days}d`;
  const m = Math.floor(days / 30);
  return m === 1 ? "1mo" : `${m}mo`;
}

const BUCKET_ORDER = ["needs_attention", "verification_required", "submitted", "closed_declined", "other"];

function MarketplaceDetail({ record }) {
  const m = BUCKET_META[record.bucket] || BUCKET_META.other;
  return (
    <div className="space-y-4" data-testid="marketplace-detail-panel">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-medium text-slate-200 tracking-tight flex items-center gap-2">
            <Linkedin size={15} className="text-sky-400 shrink-0" />
            <span className="truncate">{record.prospect_name || "Unknown requester"}</span>
          </h2>
          <p className="font-mono text-xs text-slate-500">{record.prospect_role || "—"}</p>
          {record.prospect_organization && (
            <p className="text-xs text-slate-400 mt-0.5">{record.prospect_organization}</p>
          )}
        </div>
        <Badge className={m.pill} testid="detail-bucket">{m.label}</Badge>
      </div>

      {/* Status band */}
      <div className="flex flex-wrap items-center gap-1.5 border-t border-slate-800/60 pt-3">
        {record.linkedin_request_status && (
          <Badge className={STATUS_STYLE[record.linkedin_request_status] || "bg-slate-800/60 text-slate-400 border-slate-700"}>
            LI · {record.linkedin_request_status}
          </Badge>
        )}
        {record.approval_status && (
          <Badge className="bg-slate-800/60 text-slate-300 border-slate-700">
            approval · {record.approval_status}
          </Badge>
        )}
        {record.fit_recommendation && (
          <Badge className={FIT_STYLE[record.fit_recommendation] || "bg-slate-800/60 text-slate-400 border-slate-700"}>
            fit · {record.fit_recommendation}
          </Badge>
        )}
        {record.classification && (
          <Badge className="bg-slate-900/60 text-slate-400 border-slate-800">
            {record.classification}
          </Badge>
        )}
        {record.response_urgency && (
          <Badge className="bg-slate-900/60 text-slate-400 border-slate-800">
            urgency · {record.response_urgency}
          </Badge>
        )}
        {record.material === true && <Badge className="bg-emerald-950/60 text-emerald-300 border-emerald-800/60">material</Badge>}
      </div>

      {/* Service & subject */}
      <div className="space-y-1.5">
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Requested service</div>
        <div className="text-sm text-slate-200">{record.requested_service || "—"}{record.request_kind ? ` · ${record.request_kind}` : ""}</div>
        {record.subject && (
          <>
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 pt-1">Subject</div>
            <div className="text-sm text-slate-300">{record.subject}</div>
          </>
        )}
      </div>

      {/* Recommended action */}
      {record.recommended_action && (
        <div className="border-l-2 border-sky-800/60 pl-3 py-1">
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Recommended action</div>
          <div className="text-xs text-slate-300 leading-snug mt-0.5">{record.recommended_action}</div>
        </div>
      )}

      {/* Inbound text */}
      {record.inbound_text && (
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1">
            <MessageSquare size={11} /> Inbound message
          </div>
          <pre className="whitespace-pre-wrap text-xs text-slate-300 leading-relaxed font-sans border border-slate-800 rounded-md bg-slate-950/40 p-3 max-h-56 overflow-y-auto" data-testid="detail-inbound-text">
            {record.inbound_text}
          </pre>
        </div>
      )}

      {/* Draft response (production-authored) */}
      {record.draft_response && (
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1">
            <FileText size={11} /> Draft response (production)
          </div>
          <pre className="whitespace-pre-wrap text-xs text-slate-300 leading-relaxed font-sans border border-slate-800 rounded-md bg-slate-950/40 p-3 max-h-56 overflow-y-auto" data-testid="detail-draft-response">
            {record.draft_response}
          </pre>
        </div>
      )}

      {/* Provenance / IDs — technical fields per owner directive */}
      <div className="border-t border-slate-800/60 pt-3 space-y-1 font-mono text-[11px] text-slate-400">
        <div className="flex items-start gap-2">
          <Hash size={11} className="text-slate-500 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div><Database size={9} className="inline mr-1 text-emerald-400" /> airtable rec: <span className="text-emerald-300">{record.airtable_record_id}</span></div>
            {record.received_at && <div>received: <span className="text-slate-300">{new Date(record.received_at).toLocaleString()}</span> · age: <span className="text-slate-300">{fmtAge(record.age_days)}</span></div>}
            {record.source_message_id && <div>msg-id: <span className="text-slate-300 break-all">{record.source_message_id}</span></div>}
            {record.event_key && <div>event: <span className="text-slate-300">{record.event_key}</span></div>}
            {record.mailbox && <div>mailbox: <span className="text-slate-300">{record.mailbox}</span></div>}
            {record.slack_thread_ts && <div>slack thread: <span className="text-slate-300">{record.slack_thread_ts}</span></div>}
          </div>
        </div>
        {record.source_url && (
          <a
            href={record.source_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="detail-source-link"
            className="inline-flex items-center gap-1 text-sky-400 hover:text-sky-300 pt-1"
          >
            <ExternalLink size={11} /> Open on LinkedIn
          </a>
        )}
      </div>

      {/* Read-only guardrail note */}
      <div className="flex items-start gap-2 border border-slate-800/60 bg-slate-950/40 rounded-md p-2.5" data-testid="detail-readonly-note">
        <ShieldAlert size={13} className="text-amber-400 shrink-0 mt-0.5" />
        <div className="text-[11px] text-slate-400 leading-snug">
          Read-only. Promoting a Marketplace item into the shared Opportunity lifecycle, updating approval status, or drafting a response are Phase 2 write actions pending owner approval. LinkedIn submission remains manual.
        </div>
      </div>
    </div>
  );
}

export default function MarketplaceQueue() {
  const [selectedId, setSelectedId] = useState(null);
  // Default view: Needs Attention (per owner directive — do not open on all 106).
  const [bucket, setBucket] = useState("needs_attention");
  const [filterService, setFilterService] = useState("all");
  const [filterFitRec, setFilterFitRec] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["marketplace-inbound"],
    queryFn: async () => (await api.get("/marketplace/inbound")).data,
  });

  const queue = data?.queue || [];
  const totals = data?.totals || {};
  const bucketCounts = totals.by_bucket || {};

  const filtered = useMemo(() => {
    return queue.filter((r) => {
      if (bucket !== "all" && r.bucket !== bucket) return false;
      if (filterService !== "all" && (r.requested_service || "") !== filterService) return false;
      if (filterFitRec !== "all" && (r.fit_recommendation || "") !== filterFitRec) return false;
      if (filterStatus !== "all" && (r.linkedin_request_status || "") !== filterStatus) return false;
      if (search) {
        const s = search.toLowerCase();
        const hay = [
          r.prospect_name, r.prospect_organization, r.prospect_role, r.requested_service,
          r.subject, r.inbound_text, r.airtable_record_id,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [queue, bucket, filterService, filterFitRec, filterStatus, search]);

  const serviceOptions = ["all", ...Object.keys(totals.by_requested_service || {}).filter((k) => k !== "<unset>").sort()];
  const fitRecOptions = ["all", ...Object.keys(totals.by_fit_recommendation || {}).filter((k) => k !== "<unset>")];
  const statusOptions = ["all", ...Object.keys(totals.by_linkedin_request_status || {}).filter((k) => k !== "<unset>")];

  const activeBucket = bucket === "all" ? null : BUCKET_META[bucket];

  const toggle = (id) => setSelectedId((cur) => (cur === id ? null : id));

  return (
    <div className="space-y-5" data-testid="marketplace-view">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-100 flex items-center gap-2">
            <Inbox size={22} className="text-sky-400" />
            Marketplace · Inbound Queue
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            LinkedIn Response Queue · authoritative source. Grouped by actionability for triage; underlying LinkedIn Request Status remains the source of truth.
          </p>
        </div>
        <Badge className="bg-slate-900/60 text-slate-400 border-slate-700 shrink-0" testid="marketplace-readonly-badge">
          <Lock size={10} className="mr-1" /> READ-ONLY
        </Badge>
      </div>

      {/* Bucket pills — primary triage grouping */}
      <div className="flex flex-wrap gap-2" data-testid="bucket-pills">
        {BUCKET_ORDER.map((k) => {
          const m = BUCKET_META[k];
          const active = bucket === k;
          const count = bucketCounts[k] ?? 0;
          const Icon = m.icon;
          return (
            <button
              key={k}
              onClick={() => setBucket(k)}
              data-testid={`bucket-pill-${k}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                active
                  ? `${m.pill} ring-1 ring-current/40`
                  : "bg-slate-900/60 text-slate-400 border-slate-800 hover:bg-slate-800/60 hover:text-slate-200"
              }`}
            >
              <Icon size={12} />
              {m.label}
              <span className={`font-mono text-[10px] px-1.5 py-0 rounded ${active ? "bg-current/10" : "bg-slate-950/60 text-slate-500"}`}>
                {count}
              </span>
            </button>
          );
        })}
        <button
          onClick={() => setBucket("all")}
          data-testid="bucket-pill-all"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
            bucket === "all"
              ? "bg-slate-800 text-slate-100 border-slate-600 ring-1 ring-current/20"
              : "bg-slate-900/60 text-slate-400 border-slate-800 hover:bg-slate-800/60 hover:text-slate-200"
          }`}
        >
          All
          <span className={`font-mono text-[10px] px-1.5 py-0 rounded ${bucket === "all" ? "bg-slate-700" : "bg-slate-950/60 text-slate-500"}`}>
            {totals.total ?? "—"}
          </span>
        </button>
      </div>

      {/* Bucket context banner */}
      {activeBucket && (
        <div className={`text-[11px] font-mono ${activeBucket.color} border-l-2 ${activeBucket.bg} pl-3 py-0.5`} data-testid="bucket-banner">
          {activeBucket.desc}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 border border-slate-800 bg-slate-900/40 rounded-lg p-2.5" data-testid="marketplace-filters">
        <Filter size={13} className="text-slate-500 shrink-0" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search requester, company, service, message…"
          data-testid="marketplace-search"
          className="flex-1 min-w-[240px] bg-slate-950 border border-slate-800 rounded-md px-3 py-1.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-sky-500"
        />
        <select
          value={filterService}
          onChange={(e) => setFilterService(e.target.value)}
          data-testid="filter-service"
          className="bg-slate-950 border border-slate-800 rounded-md px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-sky-500"
        >
          {serviceOptions.map((c) => <option key={c} value={c}>{c === "all" ? "All services" : c}</option>)}
        </select>
        <select
          value={filterFitRec}
          onChange={(e) => setFilterFitRec(e.target.value)}
          data-testid="filter-fit-recommendation"
          className="bg-slate-950 border border-slate-800 rounded-md px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-sky-500"
        >
          {fitRecOptions.map((c) => <option key={c} value={c}>{c === "all" ? "All fit rec." : c}</option>)}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          data-testid="filter-status"
          className="bg-slate-950 border border-slate-800 rounded-md px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-sky-500"
        >
          {statusOptions.map((c) => <option key={c} value={c}>{c === "all" ? "All LinkedIn statuses" : c}</option>)}
        </select>
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500 ml-auto">
          {filtered.length} of {queue.length} shown
        </span>
      </div>

      {/* Table with inline row expansion */}
      <div className="border border-slate-800 bg-slate-900/60 rounded-lg overflow-hidden">
        <table className="w-full text-left" data-testid="marketplace-table">
          <thead>
            <tr className="border-b border-slate-800 font-mono text-[10px] uppercase tracking-widest text-slate-500">
              <th className="px-3 py-2 w-6"></th>
              <th className="px-3 py-2">Requester</th>
              <th className="px-3 py-2">Requested Service</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Fit Rec</th>
              <th className="px-3 py-2">Recommended Action</th>
              <th className="px-3 py-2 text-right">Age</th>
              <th className="px-3 py-2">Source</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={8} className="px-3 py-4 text-sm text-slate-500">Loading…</td></tr>}
            {!isLoading && filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-sm text-slate-500 text-center">
                  {bucket === "needs_attention"
                    ? "No items currently need your attention. Nice."
                    : "No records match current filters."}
                </td>
              </tr>
            )}
            {filtered.map((r) => {
              const open = selectedId === r.airtable_record_id;
              return (
                <Fragment key={r.airtable_record_id}>
                  <tr
                    onClick={() => toggle(r.airtable_record_id)}
                    data-testid={`marketplace-row-${r.airtable_record_id}`}
                    className={`text-xs border-b border-slate-800/50 hover:bg-slate-800/40 cursor-pointer font-mono text-slate-300 ${open ? "bg-slate-800/50" : ""}`}
                  >
                    <td className="px-3 py-2 align-top text-slate-500">
                      {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    </td>
                    <td className="px-3 py-2 align-top max-w-[220px]">
                      <div className="font-sans text-sm text-slate-200 truncate">{r.prospect_name || "—"}</div>
                      <div className="text-[11px] text-slate-500 truncate">{r.prospect_organization || r.prospect_role || ""}</div>
                    </td>
                    <td className="px-3 py-2 align-top max-w-[240px]">
                      <div className="text-slate-300 line-clamp-2">{r.requested_service || r.request_kind || "—"}</div>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <Badge className={STATUS_STYLE[r.linkedin_request_status] || "bg-slate-800/60 text-slate-400 border-slate-700"}>
                        {r.linkedin_request_status || "unset"}
                      </Badge>
                      {r.approval_status && (
                        <div className="text-[10px] text-slate-500 mt-0.5">{r.approval_status}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      {r.fit_recommendation ? (
                        <Badge className={FIT_STYLE[r.fit_recommendation] || "bg-slate-800/60 text-slate-400 border-slate-700"}>
                          {r.fit_recommendation}
                        </Badge>
                      ) : <span className="text-slate-600">—</span>}
                    </td>
                    <td className="px-3 py-2 align-top max-w-[280px]">
                      <div className="text-[11px] text-slate-400 line-clamp-2 leading-snug">{r.recommended_action || "—"}</div>
                    </td>
                    <td className="px-3 py-2 align-top text-right text-slate-400" title={r.received_at || r.created_time}>
                      {fmtAge(r.age_days)}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <span className="inline-flex items-center gap-1 text-[10px] font-mono text-sky-400" title="LinkedIn Response Queue (Airtable)">
                        <Linkedin size={10} /> Marketplace
                      </span>
                    </td>
                  </tr>
                  {open && (
                    <tr data-testid={`marketplace-detail-row-${r.airtable_record_id}`}>
                      <td colSpan={8} className="p-4 bg-slate-950/60 border-b border-slate-800">
                        <MarketplaceDetail record={r} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
